import type { StaffRole } from "@lafayette/auth";
import { useGetIdentity, useLogout } from "@refinedev/core";
import { Outlet, useLocation } from "react-router";

import { useStaffRoles } from "../access/use-staff-roles";
import { getAdminPublicConfig } from "../config/public-config";
import { useAttentionSummary, useConsoleFeatures } from "../home/queries";
import { offeredAreas, offeringOf } from "../navigation/offering";
import { routeCrumbs, routeTitle } from "../navigation/route-meta";
import { AsyncState } from "../ui/async-state";
import { DensityProvider } from "../ui/density";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { Toaster } from "../ui/toaster";
import { TooltipProvider } from "../ui/tooltip";
import { CommandPalette } from "./CommandPalette";
import { useRouteDocumentTitle } from "./document-title";
import { SidebarBody } from "./SidebarBody";
import { bellState, NEW_AREA_BADGES, sidebarBadges } from "./shell-counts";
import { ShellLayoutProvider, useShellLayout } from "./shell-layout";
import {
  DesktopSidebar,
  DevelopmentIdentityBanner,
  MainRegion,
  PagePanel,
  ShellFrame,
  SkipLink,
} from "./shell-parts";
import { useSidebarCollapsed } from "./sidebar-state";
import { TopBar } from "./TopBar";
import { useDrawer } from "./use-drawer";
import { useSearchDialog } from "./use-search-dialog";

export function ProtectedLayout() {
  const { roles } = useStaffRoles();
  if (roles.length === 0) {
    return (
      <main className="grid min-h-svh place-items-center p-6">
        <AsyncState
          kind="error"
          title="Staff grant unavailable"
          message="The API did not return a trusted staff role for this session."
        />
      </main>
    );
  }
  return (
    <DensityProvider>
      <TooltipProvider>
        <ShellLayoutProvider>
          <Shell roles={roles} />
          <Toaster />
        </ShellLayoutProvider>
      </TooltipProvider>
    </DensityProvider>
  );
}

/** What the shell reads: the menu the role is offered, the counts of the sidebar and the bell, and the person who is signed in. */
function useShellData(roles: readonly StaffRole[]) {
  // The same read as Home (one request serves both). A failed read leaves no badge and no hidden entry: the menu is still complete.
  const attention = useAttentionSummary();
  const { features } = useConsoleFeatures();
  const { data: identity } = useGetIdentity<{ id: string; name: string }>();
  const offering = offeringOf(roles, features);
  const summary = attention.isError ? undefined : attention.data;
  return {
    offering,
    navigation: offeredAreas(offering),
    badges: { ...NEW_AREA_BADGES, ...sidebarBadges(summary) },
    bell: bellState(summary, offering, attention.isError),
    person: { id: identity?.id ?? "staff", name: identity?.name ?? "Staff member" },
  };
}

function Shell({ roles }: Readonly<{ roles: readonly StaffRole[] }>) {
  const config = getAdminPublicConfig();
  const { pathname, search: query } = useLocation();
  const { mutate: logout, isPending: isSigningOut } = useLogout();
  const { offering, navigation, badges, bell, person } = useShellData(roles);
  const search = useSearchDialog();
  const drawer = useDrawer(pathname);
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const { wide, fullBleed } = useShellLayout();
  useRouteDocumentTitle(routeTitle(pathname));

  const body = {
    navigation,
    pathname,
    badges,
    person,
    roles,
    environment: {
      environment: config.environment,
      version: config.version,
      buildId: config.buildId,
      gitSha: config.gitSha,
    },
    isSigningOut,
    onSignOut: () => logout(),
  };

  // A workspace that fills the window and scrolls inside its panes (the concierge workspace) asks for it with `useFullBleedLayout()`.
  return (
    <ShellFrame fullBleed={fullBleed}>
      <SkipLink />
      <DesktopSidebar collapsed={collapsed}>
        <SidebarBody {...body} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </DesktopSidebar>
      <Sheet open={drawer.open} onOpenChange={drawer.setOpen}>
        <SheetContent onCloseAutoFocus={drawer.onCloseAutoFocus}>
          <SheetTitle className="sr-only">Staff navigation menu</SheetTitle>
          <SheetDescription className="sr-only">
            The areas of the staff console you may use.
          </SheetDescription>
          <SidebarBody {...body} collapsed={false} onNavigate={() => drawer.setOpen(false)} />
        </SheetContent>
      </Sheet>
      <PagePanel fullBleed={fullBleed}>
        <TopBar
          crumbs={routeCrumbs(pathname, offering, query)}
          menuOpen={drawer.open}
          menuButtonRef={drawer.menuButton}
          onOpenMenu={() => drawer.setOpen(true)}
          onOpenSearch={search.openSearch}
          searchOpen={search.open}
          bell={bell}
        />
        {config.developmentAuthPlaceholderEnabled ? <DevelopmentIdentityBanner /> : null}
        <MainRegion wide={wide} fullBleed={fullBleed}>
          <Outlet />
        </MainRegion>
      </PagePanel>
      <CommandPalette
        open={search.open}
        onOpenChange={search.setOpen}
        opener={search.opener}
        areas={navigation}
      />
    </ShellFrame>
  );
}
